/**
 * Adjustments View Component
 * Handles loading and visualizing LoadSEER adjustments (Spatial, User, New Growth)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { wrapTo, addDataToMap, addNotification } from '@kepler.gl/actions';
import { processGeojson } from '@kepler.gl/processors';
import styled from 'styled-components';
import type { ResourceViewProps, ScenarioDto, NodeTypeDto } from '../../types';
import { AdjustmentsLoadManager } from '../../loading/adjustments-load-manager';
import type {
  AdjustmentLoadSummary,
  LoadProgressSnapshot,
} from '../../loading/adjustments-load-manager';
import { adjustmentsToGeoJSON } from '../../utils/adjustments-converter';
import { filterGridAssetNodeTypes } from '../../utils/node-type-helpers';
import { enrichWithUpstreamNodes } from '../../services/upstream-node-resolver';

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  margin: 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${props => props.theme?.borderColor || '#30363d'};
`;

const Label = styled.label`
  font-size: 0.875rem;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  padding: 0.5rem;
  background: ${props => props.theme?.inputBgd || '#1c2128'};
  color: ${props => props.theme?.textColor || '#e6edf3'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.primaryBtnBgd || '#238636'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  cursor: pointer;

  input {
    cursor: pointer;
  }
`;

const MultiSelect = styled.select`
  padding: 0.5rem;
  background: ${props => props.theme?.inputBgd || '#1c2128'};
  color: ${props => props.theme?.textColor || '#e6edf3'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  font-size: 0.875rem;
  min-height: 120px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.primaryBtnBgd || '#238636'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme?.primaryBtnBgd || '#238636'};
  color: #ffffff;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme?.primaryBtnBgdHover || '#2ea043'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProgressContainer = styled.div`
  padding: 1rem;
  background: ${props => props.theme?.panelBackground || '#161b22'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
`;

const ProgressText = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  margin-bottom: 0.5rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: ${props => props.theme?.primaryBtnBgd || '#238636'};
  width: ${props => props.progress}%;
  transition: width 0.3s ease;
`;

const SummaryCard = styled.div`
  padding: 1rem;
  background: ${props => props.theme?.panelBackground || '#161b22'};
  border: 1px solid ${props => props.theme?.borderColor || '#30363d'};
  border-radius: 4px;
`;

const SummaryTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme?.textColor || '#e6edf3'};
  margin-bottom: 0.75rem;
`;

const SummaryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SummaryItem = styled.li`
  font-size: 0.875rem;
  color: ${props => props.theme?.textColor || '#8b949e'};
  display: flex;
  justify-content: space-between;
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme?.errorColor || '#f85149'}22;
  color: ${props => props.theme?.errorColor || '#f85149'};
  border: 1px solid ${props => props.theme?.errorColor || '#f85149'}66;
  border-radius: 4px;
  font-size: 0.875rem;
`;

// ============================================================================
// Component
// ============================================================================

export const AdjustmentsView: FC<ResourceViewProps> = ({
  apiClient,
  isLoading,
  setIsLoading,
  onLoadComplete,
}) => {
  const dispatch = useDispatch<Dispatch>();

  // Scenario state
  const [scenarios, setScenarios] = useState<ScenarioDto[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);
  const [scenariosLoading, setScenariosLoading] = useState(false);

  // Adjustment type state
  const [selectedTypes, setSelectedTypes] = useState<Set<'spatial' | 'user' | 'newGrowth'>>(
    new Set(['spatial', 'user', 'newGrowth'])
  );

  // Run selection state
  const [selectedRunIds, setSelectedRunIds] = useState<Set<number>>(new Set());

  // Node type state for upstream discovery
  const [nodeTypes, setNodeTypes] = useState<NodeTypeDto[]>([]);
  const [nodeTypesLoading, setNodeTypesLoading] = useState(false);
  const [enableUpstreamDiscovery, setEnableUpstreamDiscovery] = useState(false);
  const [selectedNodeTypeCode, setSelectedNodeTypeCode] = useState<string | null>(null);
  const [upstreamProgress, setUpstreamProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);

  // Refs to capture state at load time (not at useEffect mount time)
  const upstreamSettingsRef = useRef({ enabled: false, nodeTypeCode: null as string | null });
  const nodeTypesRef = useRef<NodeTypeDto[]>([]);

  // Update refs when settings change
  useEffect(() => {
    upstreamSettingsRef.current = {
      enabled: enableUpstreamDiscovery,
      nodeTypeCode: selectedNodeTypeCode,
    };
  }, [enableUpstreamDiscovery, selectedNodeTypeCode]);

  useEffect(() => {
    nodeTypesRef.current = nodeTypes;
  }, [nodeTypes]);

  // Loading state
  const [loadManager, setLoadManager] = useState<AdjustmentsLoadManager | null>(null);
  const [loadProgress, setLoadProgress] = useState<LoadProgressSnapshot | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lastSummary, setLastSummary] = useState<
    (AdjustmentLoadSummary & { upstreamEnriched?: number; upstreamFailed?: number }) | null
  >(null);

  // Initialize load manager
  useEffect(() => {
    const manager = new AdjustmentsLoadManager(apiClient);

    const unsubscribeProgress = manager.subscribeProgress(progress => {
      setLoadProgress(progress);
    });

    const unsubscribeComplete = manager.subscribeComplete(async (adjustments, summary) => {
      if (import.meta.env.DEV) {
        console.log('[AdjustmentsView] Load complete callback triggered', {
          adjustmentsCount: adjustments.length,
          upstreamSettings: upstreamSettingsRef.current,
          nodeTypesCount: nodeTypesRef.current.length,
        });
      }

      let finalAdjustments = adjustments;
      let enrichedCount = 0;
      let failedCount = 0;

      // Perform upstream node discovery if enabled
      const { enabled: upstreamEnabled, nodeTypeCode: upstreamNodeTypeCode } =
        upstreamSettingsRef.current;

      if (upstreamEnabled && upstreamNodeTypeCode) {
        const selectedNodeType = nodeTypesRef.current.find(nt => nt.code === upstreamNodeTypeCode);

        if (import.meta.env.DEV) {
          console.log('[AdjustmentsView] Starting upstream discovery', {
            nodeTypeCode: upstreamNodeTypeCode,
            nodeTypeFound: !!selectedNodeType,
            selectedNodeType: selectedNodeType
              ? { code: selectedNodeType.code, name: selectedNodeType.name }
              : null,
            availableNodeTypes: nodeTypesRef.current.map(nt => ({ code: nt.code, name: nt.name })),
          });
        }

        if (selectedNodeType) {
          try {
            setUpstreamProgress({ completed: 0, total: 0 });

            const enrichmentResult = await enrichWithUpstreamNodes(
              adjustments,
              selectedNodeType,
              apiClient,
              (completed, total) => {
                if (import.meta.env.DEV) {
                  console.log('[AdjustmentsView] Upstream progress:', { completed, total });
                }
                setUpstreamProgress({ completed, total });
              }
            );

            if (import.meta.env.DEV) {
              console.log('[AdjustmentsView] Upstream enrichment complete', {
                enrichedCount: enrichmentResult.enriched.length,
                failedCount: enrichmentResult.failed.length,
                stats: enrichmentResult.stats,
              });
            }

            finalAdjustments = enrichmentResult.enriched;
            enrichedCount = enrichmentResult.stats.succeeded;
            failedCount = enrichmentResult.stats.failed;

            // Show partial failure warning if any failed
            if (failedCount > 0) {
              dispatch(
                wrapTo(
                  'map',
                  addNotification({
                    message: `Upstream discovery: ${enrichedCount} succeeded, ${failedCount} failed (no matching ancestor)`,
                    type: 'warning',
                  })
                )
              );
            }
          } catch (error) {
            console.error('[AdjustmentsView] Upstream enrichment error:', error);
            dispatch(
              wrapTo(
                'map',
                addNotification({
                  message: `Upstream discovery failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                  type: 'error',
                })
              )
            );
          } finally {
            setUpstreamProgress(null);
          }
        } else {
          console.warn(
            '[AdjustmentsView] Upstream enabled but node type not found:',
            upstreamNodeTypeCode
          );
        }
      } else if (import.meta.env.DEV) {
        console.log('[AdjustmentsView] Upstream discovery not enabled or node type not selected');
      }

      setIsLoading(false);
      setLoadProgress(null);
      setLastSummary({
        ...summary,
        upstreamEnriched: enrichedCount > 0 ? enrichedCount : undefined,
        upstreamFailed: failedCount > 0 ? failedCount : undefined,
      });

      // Convert to GeoJSON
      if (import.meta.env.DEV) {
        console.log('[AdjustmentsView] Converting to GeoJSON', {
          adjustmentsCount: finalAdjustments.length,
          sampleAdjustment: finalAdjustments[0],
        });
      }

      const { features, statistics } = adjustmentsToGeoJSON(finalAdjustments);

      if (import.meta.env.DEV) {
        console.log('[AdjustmentsView] GeoJSON conversion complete', {
          featureCount: features.features.length,
          sampleFeatureProperties: features.features[0]?.properties,
          statistics,
        });
      }

      // Add to Kepler.gl
      const datasetId = `loadseer-adjustments-${Date.now()}`;
      const processedData = processGeojson(features);

      if (processedData) {
        const datasets = {
          info: {
            id: datasetId,
            label: `Adjustments: ${summary.scenarioName}${enableUpstreamDiscovery ? ' (with upstream)' : ''}`,
          },
          data: processedData,
        };

        dispatch(wrapTo('map', addDataToMap({ datasets })));

        // Success notification
        const message =
          enableUpstreamDiscovery && enrichedCount > 0
            ? `Loaded ${summary.totalLoaded} adjustments with ${enrichedCount} upstream nodes (${statistics.excludedNoLocation} excluded without location)`
            : `Loaded ${summary.totalLoaded} adjustments (${statistics.excludedNoLocation} excluded without location)`;

        dispatch(
          wrapTo(
            'map',
            addNotification({
              message,
              type: 'success',
            })
          )
        );
      }

      onLoadComplete();
    });

    setLoadManager(manager);

    return () => {
      unsubscribeProgress();
      unsubscribeComplete();
    };
  }, [
    apiClient,
    dispatch,
    setIsLoading,
    onLoadComplete,
    enableUpstreamDiscovery,
    selectedNodeTypeCode,
    nodeTypes,
  ]);

  // Load scenarios and node types on mount
  useEffect(() => {
    const loadScenarios = async () => {
      setScenariosLoading(true);
      setLoadError(null);

      try {
        const response = await apiClient.getScenarios({ page: 1, pageSize: 1000 });
        const sortedScenarios = response.results
          .filter(s => s.isActive)
          .sort((a, b) => a.name.localeCompare(b.name));
        setScenarios(sortedScenarios);
      } catch (error) {
        setLoadError(error instanceof Error ? error.message : 'Failed to load scenarios');
      } finally {
        setScenariosLoading(false);
      }
    };

    const loadNodeTypes = async () => {
      setNodeTypesLoading(true);
      try {
        const response = await apiClient.getNodeTypes();

        if (import.meta.env.DEV) {
          console.log('[AdjustmentsView] Node types loaded:', {
            totalCount: response.results.length,
            sampleTypes: response.results.slice(0, 3).map(nt => ({
              code: nt.code,
              name: nt.name,
              isActive: nt.isActive,
              labels: nt.defaultLabels,
            })),
          });
        }

        const gridAssetTypes = filterGridAssetNodeTypes(response.results)
          .filter(nt => nt.isActive)
          .sort((a, b) => a.name.localeCompare(b.name));

        if (import.meta.env.DEV) {
          console.log('[AdjustmentsView] Filtered grid asset types:', {
            count: gridAssetTypes.length,
            types: gridAssetTypes.map(nt => ({
              code: nt.code,
              name: nt.name,
              labels: nt.defaultLabels,
            })),
          });
        }

        setNodeTypes(gridAssetTypes);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('Failed to load node types:', error);
        }
      } finally {
        setNodeTypesLoading(false);
      }
    };

    loadScenarios();
    loadNodeTypes();
  }, [apiClient]);

  // Update runs when scenario changes
  useEffect(() => {
    setSelectedRunIds(new Set());
  }, [selectedScenarioId]);

  // Handle adjustment type toggle
  const handleTypeToggle = useCallback((type: 'spatial' | 'user' | 'newGrowth') => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Handle run selection toggle (reserved for future checkbox implementation)
  const _handleRunToggle = useCallback((runId: number, selected: boolean) => {
    setSelectedRunIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(runId);
      } else {
        next.delete(runId);
      }
      return next;
    });
  }, []);

  // Handle load button click
  const handleLoad = useCallback(async () => {
    if (!selectedScenarioId || !loadManager) return;

    setIsLoading(true);
    setLoadError(null);
    setLastSummary(null);

    try {
      await loadManager.loadAdjustments({
        scenarioId: selectedScenarioId,
        adjustmentTypes: Array.from(selectedTypes),
        runIds: Array.from(selectedRunIds),
      });
    } catch (error) {
      setIsLoading(false);
      setLoadProgress(null);
      setLoadError(error instanceof Error ? error.message : 'Failed to load adjustments');

      dispatch(
        wrapTo(
          'map',
          addNotification({
            message: 'Failed to load adjustments',
            type: 'error',
          })
        )
      );
    }
  }, [selectedScenarioId, selectedTypes, selectedRunIds, loadManager, setIsLoading, dispatch]);

  // Get selected scenario
  const selectedScenario = scenarios.find(s => s.id === selectedScenarioId);

  // Get available runs for selected scenario
  const availableRuns = selectedScenario
    ? Object.entries(selectedScenario.runIdsAndNames)
        .map(([id, name]) => ({ id: Number(id), name }))
        .sort((a, b) => a.name.localeCompare(b.name))
    : [];

  // Determine if run selector should be disabled
  const runSelectorDisabled = selectedTypes.size === 1 && selectedTypes.has('newGrowth');

  // Determine if load button should be enabled
  const loadButtonEnabled =
    selectedScenarioId !== null &&
    selectedTypes.size > 0 &&
    (runSelectorDisabled || selectedRunIds.size > 0) &&
    (!enableUpstreamDiscovery || selectedNodeTypeCode !== null) &&
    !isLoading;

  return (
    <Container>
      {/* Scenario Selection */}
      <Section>
        <SectionTitle>Scenario</SectionTitle>
        <Label htmlFor='scenario-select'>Select Scenario *</Label>
        <Select
          id='scenario-select'
          value={selectedScenarioId ?? ''}
          onChange={e => setSelectedScenarioId(Number(e.target.value))}
          disabled={scenariosLoading || isLoading}
        >
          <option value=''>-- Select a scenario --</option>
          {scenarios.map(scenario => (
            <option key={scenario.id} value={scenario.id}>
              {scenario.name} {scenario.code && `(${scenario.code})`}
            </option>
          ))}
        </Select>
      </Section>

      {/* Adjustment Type Selection */}
      <Section>
        <SectionTitle>Adjustment Types</SectionTitle>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type='checkbox'
              checked={selectedTypes.has('spatial')}
              onChange={() => handleTypeToggle('spatial')}
              disabled={isLoading}
            />
            Spatial Adjustments
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type='checkbox'
              checked={selectedTypes.has('user')}
              onChange={() => handleTypeToggle('user')}
              disabled={isLoading}
            />
            User Adjustments
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type='checkbox'
              checked={selectedTypes.has('newGrowth')}
              onChange={() => handleTypeToggle('newGrowth')}
              disabled={isLoading}
            />
            New Growth Adjustments
          </CheckboxLabel>
        </CheckboxGroup>
      </Section>

      {/* Upstream Node Discovery */}
      <Section>
        <SectionTitle>Upstream Node Discovery (Optional)</SectionTitle>
        <CheckboxLabel>
          <input
            type='checkbox'
            checked={enableUpstreamDiscovery}
            onChange={e => {
              setEnableUpstreamDiscovery(e.target.checked);
              if (!e.target.checked) {
                setSelectedNodeTypeCode(null);
              }
            }}
            disabled={isLoading || nodeTypesLoading}
          />
          Find upstream node of specific type
        </CheckboxLabel>
        {enableUpstreamDiscovery && (
          <>
            <Label htmlFor='node-type-select'>Node Type *</Label>
            <Select
              id='node-type-select'
              value={selectedNodeTypeCode ?? ''}
              onChange={e => setSelectedNodeTypeCode(e.target.value || null)}
              disabled={isLoading || nodeTypesLoading}
            >
              <option value=''>-- Select a node type --</option>
              {nodeTypes.map(nodeType => (
                <option key={nodeType.code} value={nodeType.code}>
                  {nodeType.name}
                </option>
              ))}
            </Select>
            <div style={{ fontSize: '0.75rem', color: '#8b949e' }}>
              Finds the first upstream (parent) node matching the selected type
            </div>
          </>
        )}
      </Section>

      {/* Run Selection */}
      {selectedScenario && (
        <Section>
          <SectionTitle>Runs</SectionTitle>
          <Label htmlFor='run-select'>
            Select Runs {!runSelectorDisabled && '*'}
            {runSelectorDisabled && ' (Not required for New Growth only)'}
          </Label>
          <MultiSelect
            id='run-select'
            multiple
            value={Array.from(selectedRunIds).map(String)}
            onChange={e => {
              const selected = Array.from(e.target.selectedOptions).map(opt => Number(opt.value));
              setSelectedRunIds(new Set(selected));
            }}
            disabled={runSelectorDisabled || isLoading}
          >
            {availableRuns.map(run => (
              <option key={run.id} value={run.id}>
                {run.name}
              </option>
            ))}
          </MultiSelect>
          <div style={{ fontSize: '0.75rem', color: '#8b949e' }}>
            Hold Ctrl/Cmd to select multiple runs
          </div>
        </Section>
      )}

      {/* Load Button */}
      <LoadButton onClick={handleLoad} disabled={!loadButtonEnabled}>
        {isLoading ? 'Loading...' : 'Load Adjustments'}
      </LoadButton>

      {/* Progress Display */}
      {loadProgress && (
        <ProgressContainer>
          <ProgressText>{loadProgress.currentSource || 'Loading adjustments...'}</ProgressText>
          <ProgressText>
            {loadProgress.completed} of {loadProgress.total} requests completed
            {loadProgress.failed > 0 && ` (${loadProgress.failed} failed)`}
          </ProgressText>
          <ProgressBar>
            <ProgressFill
              progress={
                loadProgress.total > 0
                  ? ((loadProgress.completed + loadProgress.failed) / loadProgress.total) * 100
                  : 0
              }
            />
          </ProgressBar>
        </ProgressContainer>
      )}

      {/* Upstream Progress Display */}
      {upstreamProgress && (
        <ProgressContainer>
          <ProgressText>Resolving upstream nodes...</ProgressText>
          <ProgressText>
            {upstreamProgress.completed} of {upstreamProgress.total} unique nodes
          </ProgressText>
          <ProgressBar>
            <ProgressFill
              progress={
                upstreamProgress.total > 0
                  ? (upstreamProgress.completed / upstreamProgress.total) * 100
                  : 0
              }
            />
          </ProgressBar>
        </ProgressContainer>
      )}

      {/* Summary Display */}
      {lastSummary && (
        <SummaryCard>
          <SummaryTitle>Load Summary</SummaryTitle>
          <SummaryList>
            <SummaryItem>
              <span>Scenario:</span>
              <strong>{lastSummary.scenarioName}</strong>
            </SummaryItem>
            <SummaryItem>
              <span>Total Loaded:</span>
              <strong>{lastSummary.totalLoaded.toLocaleString()}</strong>
            </SummaryItem>
            {lastSummary.bySources.spatial > 0 && (
              <SummaryItem>
                <span>Spatial:</span>
                <strong>{lastSummary.bySources.spatial.toLocaleString()}</strong>
              </SummaryItem>
            )}
            {lastSummary.bySources.user > 0 && (
              <SummaryItem>
                <span>User:</span>
                <strong>{lastSummary.bySources.user.toLocaleString()}</strong>
              </SummaryItem>
            )}
            {lastSummary.bySources.newGrowth > 0 && (
              <SummaryItem>
                <span>New Growth:</span>
                <strong>{lastSummary.bySources.newGrowth.toLocaleString()}</strong>
              </SummaryItem>
            )}
            {lastSummary.duplicatesRemoved > 0 && (
              <SummaryItem>
                <span>Duplicates Removed:</span>
                <strong>{lastSummary.duplicatesRemoved.toLocaleString()}</strong>
              </SummaryItem>
            )}
            {lastSummary.excludedNoLocation > 0 && (
              <SummaryItem>
                <span>Excluded (No Location):</span>
                <strong>{lastSummary.excludedNoLocation.toLocaleString()}</strong>
              </SummaryItem>
            )}
            {lastSummary.upstreamEnriched !== undefined && lastSummary.upstreamEnriched > 0 && (
              <SummaryItem>
                <span>Upstream Nodes Found:</span>
                <strong>{lastSummary.upstreamEnriched.toLocaleString()}</strong>
              </SummaryItem>
            )}
            {lastSummary.upstreamFailed !== undefined && lastSummary.upstreamFailed > 0 && (
              <SummaryItem>
                <span>No Upstream Match:</span>
                <strong>{lastSummary.upstreamFailed.toLocaleString()}</strong>
              </SummaryItem>
            )}
          </SummaryList>
        </SummaryCard>
      )}

      {/* Error Display */}
      {loadError && <ErrorMessage>{loadError}</ErrorMessage>}
    </Container>
  );
};
